import {
  Box,
  CircularProgress,
  Divider,
  List,
  ListItem,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { IAISystem } from 'modules/ai-inventory/ai-system/components/AISystemRecorder';
import { IDataset } from 'modules/ai-inventory/datasets/records-dataset/Main';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { DatasetsService } from 'services';
import { RiskRegisterService } from 'services/RiskRegisterService';
import {
  AppPrimaryButton,
  AppPrimaryButtonOutlined,
  AppSelectWithKeys,
} from 'shared/components/AppComponents';
import { BaseApplicationState } from 'state/baseapplication/Reducer';

interface ILinkedDatasetsProps {
  linkedDatasets: IDataset[];
  riskId: string;
  reloadDetail: () => void;
}

const LinkedDatasets: React.FC<ILinkedDatasetsProps> = (props) => {
  const { linkedDatasets, riskId, reloadDetail } = props;
  const [isLoadingRecords, setIsLoadingRecords] = useState(false);
  const [records, setRecords] = useState<IAISystem[]>([]);
  const [selectedRecordId, setSelectedRecordId] = useState('');
  const [isLinking, setIsLinking] = useState('');

  const { orgACLConfig }: BaseApplicationState = useSelector(
    (state: any) => state.baseAppState
  );

  const theme = useTheme();

  // API Calls
  const loadDatasets = (): void => {
    setIsLoadingRecords(true);
    const queryString = new URLSearchParams({
      offset: '0',
      limit: '1000',
    }).toString();
    DatasetsService.getDatasets(queryString)
      .then((response) => {
        const { datasets } = response.data.data;
        /* eslint no-underscore-dangle: 0 */
        setRecords(datasets.map((d: any) => ({ ...d, id: d._id })));
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => {
        setIsLoadingRecords(false);
      });
  };

  const linkRecord = (payload: any, callback: any): void => {
    RiskRegisterService.linkDataset(riskId, payload)
      .then((response) => {
        callback(response);
      })
      .catch(() => {
        callback(false);
      });
  };

  // UI Actions
  const actionOnLink = (status: string, id: string): void => {
    setIsLinking(status);
    linkRecord(
      {
        datasetId: id,
        action: status,
      },
      (response: any) => {
        setIsLinking('');
        setSelectedRecordId('');
        if (typeof response === 'object') {
          reloadDetail();
        }
      }
    );
  };

  // Hooks
  useEffect(() => {
    loadDatasets();
  }, []);

  // UI Elements
  return (
    <Box className="app-plain-card">
      <Box className="title">
        <Typography variant="h4">Linked Datasets</Typography>
      </Box>

      <Box className="content">
        {isLoadingRecords && (
          <Box className="empty-content">
            <CircularProgress />
          </Box>
        )}

        {!isLoadingRecords && (
          <>
            <Stack
              direction="row"
              spacing={4}
              alignItems="center"
              sx={{
                [theme.breakpoints.up('lg')]: {
                  width: '50%',
                },
              }}
            >
              <AppSelectWithKeys
                className
                label=""
                onChange={(e: any) => {
                  setSelectedRecordId(e.target.value);
                }}
                items={records.filter(
                  (r) => !linkedDatasets.some((ls) => ls._id === r._id)
                )}
                titleKey="datasetName"
                valueKey="_id"
              />
              {orgACLConfig.riskManagement?.includes(
                'riskManagement.riskRegister.view.linkedDatasets.link'
              ) && (
                <AppPrimaryButtonOutlined
                  isLoading={isLinking === 'link'}
                  disabled={!selectedRecordId}
                  title="Link"
                  onClick={() => {
                    actionOnLink('link', selectedRecordId);
                  }}
                />
              )}
            </Stack>
            <List>
              {linkedDatasets.map((dataset) => (
                <>
                  <ListItem>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      sx={{ width: '100%' }}
                    >
                      <Stack>
                        <Typography className="f18 fw-6 mb-2">
                          {dataset.datasetName}
                        </Typography>
                        <Typography variant="body1">
                          {dataset.owningTeam}
                        </Typography>
                      </Stack>
                      {orgACLConfig.riskManagement?.includes(
                        'riskManagement.riskRegister.view.linkedDatasets.unlink'
                      ) && (
                        <AppPrimaryButton
                          isLoading={selectedRecordId === dataset._id}
                          onClick={() => {
                            setSelectedRecordId(dataset._id);
                            actionOnLink('unlink', dataset._id);
                          }}
                          className="btn-danger btn-small"
                          title="Unlink"
                        />
                      )}
                    </Stack>
                  </ListItem>
                  <Divider />
                </>
              ))}
            </List>
          </>
        )}
      </Box>
    </Box>
  );
};

export default LinkedDatasets;
